import { fetchJSON as fetchAPI } from './client';

export type CommercialDashboard = {
  tenant_id: string;
  workspace_id?: string;
  active_subscription?: Subscription;
  current_plan?: PricingPlan;
  credit_balance: number;
  credit_available: number;
  usage_this_period?: Record<string, number>;
  pending_invoices: number;
  unpaid_amount_cents: number;
  trial_days_remaining: number;
  entitlements_active: number;
  unread_notifications: number;
  recent_activity?: ActivityEntry[];
  generated_at: string;
};

export type PricingPlan = {
  id: string;
  name: string;
  type: string;
  price_cents: number;
  currency: string;
  credit_grant?: number;
  features?: { key: string; name?: string; enabled: boolean; limit?: number }[];
  billing_cycle: string;
  limits?: {
    workspaces: number;
    brands: number;
    users: number;
    storage_gb: number;
    api_calls_monthly: number;
    ai_limit_monthly: number;
    marketplace_connectors: number;
  };
  support_tier?: string;
  featured?: boolean;
};

export type Subscription = {
  id: string;
  tenant_id: string;
  workspace_id?: string;
  plan_id: string;
  status: string;
  trial_ends_at?: string;
  current_period_start: string;
  current_period_end: string;
  created_at: string;
};

export type InvoiceRecord = {
  id: string;
  invoice_number: string;
  type: string;
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  currency: string;
  status: string;
  description?: string;
  issued_at: string;
  paid_at?: string;
};

export type PaymentRecord = {
  id: string;
  invoice_id?: string;
  provider: string;
  status: string;
  amount_cents: number;
  currency: string;
  created_at: string;
};

export type CreditAccount = {
  balance: number;
  reserved: number;
  available: number;
  status: string;
};

export type CreditTransaction = {
  id: string;
  type: string;
  amount: number;
  balance_after: number;
  description?: string;
  created_at: string;
};

export type RevenueAnalytics = {
  mrr: number;
  arr: number;
  churn_rate: number;
  arpu: number;
  ltv: number;
  credit_consumption: number;
  revenue_by_plan?: Record<string, number>;
  feature_adoption?: Record<string, number>;
};

export type UsageDashboard = {
  by_type: Record<string, number>;
  by_module?: Record<string, number>;
  period_start: string;
  period_end: string;
};

export type Coupon = {
  id: string;
  code: string;
  type: string;
  value: number;
  max_uses: number;
  used_count: number;
  active: boolean;
};

export type Entitlement = {
  feature_key: string;
  enabled: boolean;
  limit?: number;
  used?: number;
};

export type ActivityEntry = {
  id: string;
  category: string;
  action: string;
  message: string;
  created_at: string;
};

export type CommercialNotification = {
  id: string;
  title: string;
  body?: string;
  severity?: string;
  read: boolean;
  created_at: string;
};

const BILLING_BASE = import.meta.env.VITE_BILLING_API ?? '/api/v1/billing';
const COMMERCIAL_BASE = import.meta.env.VITE_COMMERCIAL_API ?? '/api/v1/commercial';

function billingFetch<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(BILLING_BASE, path, init);
}

function commercialFetch<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(COMMERCIAL_BASE, path, init);
}

export const billingApi = {
  dashboard: (workspaceId?: string) =>
    billingFetch<CommercialDashboard>(`/dashboard${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  pricing: () => billingFetch<PricingPlan[]>('/pricing'),

  revenueAnalytics: () => billingFetch<RevenueAnalytics>('/analytics/revenue'),

  usageDashboard: (workspaceId?: string) =>
    billingFetch<UsageDashboard>(`/usage/dashboard${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  checkout: (body: { plan_id: string; workspace_id?: string; coupon_code?: string; provider?: string }) =>
    billingFetch<{ checkout_id: string; payment_id: string; invoice_id: string; total_cents: number; status: string }>(
      '/checkout',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  listInvoices: (limit = 20) => billingFetch<InvoiceRecord[]>(`/invoices?limit=${limit}`),

  listPayments: (limit = 20) => billingFetch<PaymentRecord[]>(`/payments?limit=${limit}`),

  paymentProviders: () => billingFetch<string[]>('/payment-providers'),

  createCoupon: (body: { code: string; type: string; value: number; max_uses?: number }) =>
    billingFetch<Coupon>('/coupons', { method: 'POST', body: JSON.stringify(body) }),

  listCoupons: () => billingFetch<Coupon[]>('/coupons'),

  upgradeSubscription: (id: string, targetPlanId: string) =>
    billingFetch<Subscription>(`/subscriptions/${id}/upgrade`, {
      method: 'POST',
      body: JSON.stringify({ target_plan_id: targetPlanId }),
    }),

  pauseSubscription: (id: string) =>
    billingFetch<Subscription>(`/subscriptions/${id}/pause`, { method: 'POST' }),

  resumeSubscription: (id: string) =>
    billingFetch<Subscription>(`/subscriptions/${id}/resume`, { method: 'POST' }),

  checkModuleEntitlement: (module: string, workspaceId?: string) =>
    billingFetch<{ allowed: boolean }>('/entitlements/check-module', {
      method: 'POST',
      body: JSON.stringify({ module, workspace_id: workspaceId }),
    }),

  listNotifications: (unread = true) => billingFetch<CommercialNotification[]>(`/notifications?unread=${unread}`),

  listActivity: (limit = 15) => billingFetch<ActivityEntry[]>(`/activity?limit=${limit}`),

  adminHealth: () => billingFetch<Record<string, unknown>>('/admin/health'),

  // IMP commercial APIs (credits, subscriptions, usage)
  subscribe: (body: { plan_id: string; workspace_id?: string }) =>
    commercialFetch<Subscription>('/subscriptions', { method: 'POST', body: JSON.stringify(body) }),

  listSubscriptions: () => commercialFetch<Subscription[]>('/subscriptions'),

  creditBalance: (workspaceId?: string) =>
    commercialFetch<CreditAccount>(`/credits/balance${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  listCreditTransactions: (limit = 20) =>
    commercialFetch<CreditTransaction[]>(`/credits/transactions?limit=${limit}`),

  listEntitlements: () => commercialFetch<Entitlement[]>('/entitlements'),

  listPlans: () => commercialFetch<PricingPlan[]>('/plans'),

  // DEV-000037 spec routes
  dev037Dashboard: (workspaceId?: string) =>
    fetchAPI<CommercialDashboard>('/api/v1', `/commercial/dashboard${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  listBundles: () => fetchAPI<CommercialBundle[]>('/api/v1', '/bundles'),

  listModuleLicenses: () => fetchAPI<ModuleLicense[]>('/api/v1', '/modules/licenses'),

  activateModuleLicense: (body: { module_id: string; bundle_code?: string; trial_days?: number }) =>
    fetchAPI<ModuleLicense>('/api/v1', '/licenses/activate', { method: 'POST', body: JSON.stringify(body) }),

  deactivateModuleLicense: (moduleId: string) =>
    fetchAPI<void>('/api/v1', '/licenses/deactivate', { method: 'POST', body: JSON.stringify({ module_id: moduleId }) }),

  purchaseCredits: (amount: number, workspaceId?: string) =>
    fetchAPI<CreditTransaction>('/api/v1', '/credits/purchase', {
      method: 'POST',
      body: JSON.stringify({ amount, workspace_id: workspaceId }),
    }),

  applyCoupon: (code: string, amountCents: number) =>
    fetchAPI<{ amount_cents: number; bonus_credits: number }>('/api/v1', '/coupons/apply', {
      method: 'POST',
      body: JSON.stringify({ code, amount_cents: amountCents }),
    }),
};

export type ModuleLicense = {
  id: string;
  module_id: string;
  license_key: string;
  status: string;
  bundle_code?: string;
  activated_at?: string;
};

export type CommercialBundle = {
  code: string;
  name: string;
  description?: string;
  modules: string[];
  features: string[];
  credit_grant?: number;
  featured?: boolean;
};
